/**
 * Post-create orchestration for the Product Create wizard.
 *
 * A product create is a sequence, not a single write:
 *   canonical facts -> draft media transfer -> supplier link -> channel drafts
 *   -> per-channel primary image.
 *
 * This module owns the *ordering and completion* semantics only. It performs no
 * I/O and never talks to a provider. The wizard passes in the raw results and
 * gets back structured, account-scoped outcomes so a retry can target only the
 * steps that are still unresolved.
 *
 * Identity rules:
 *   - a channel step is keyed by connector id + stage, never by display name,
 *     so two eBay accounts with identical names or error text stay distinct;
 *   - a listing result is only ever `adopted` when the existing listing was
 *     proven to belong to the expected organisation, product and connector.
 */

import type { WizardChannelIntent } from "@/lib/wizard-selling";
import type { PostCreateObligations } from "@/lib/wizard-create-recovery";

export type ChannelStage = "listing" | "primary_media";

/** `created` = this attempt wrote it, `adopted` = an existing matching row was reused. */
export type ChannelOutcomeStatus = "created" | "adopted" | "failed";

export interface ChannelOutcome {
  connectorId: string;
  provider: string;
  displayName: string;
  /** Display identity, account-scoped. Never used for matching. */
  channel: string;
  stage: ChannelStage;
  status: ChannelOutcomeStatus;
  listingId: string | null;
  message: string | null;
  /**
   * The product this outcome belongs to. Outcomes are scoped to one creation
   * attempt AND one product, so a late retry from an abandoned attempt can
   * never supply a listing id for a different product.
   */
  productId?: string | null;
}

/** Display key: one row per account + stage. Stable across renders. */
export function channelOutcomeKey(connectorId: string, stage: ChannelStage): string {
  return `${connectorId}:${stage}`;
}

/**
 * Identity used for merging/completion decisions. Product-scoped when known,
 * so two products' outcomes can never overwrite each other in one array.
 * A legacy outcome with no product id keeps the previous, product-free key.
 */
export function channelOutcomeIdentity(outcome: ChannelOutcome): string {
  const product = outcome.productId ?? "*";
  return `${product}:${outcome.connectorId}:${outcome.stage}`;
}

export function findChannelOutcome(
  outcomes: ChannelOutcome[],
  connectorId: string,
  stage: ChannelStage,
  productId?: string | null,
): ChannelOutcome | undefined {
  return outcomes.find((row) => row.connectorId === connectorId
    && row.stage === stage
    && (!productId || !row.productId || row.productId === productId));
}

/** A stage is resolved when it produced a listing (created/adopted) or a successful apply. */
export function channelStageResolved(outcome: ChannelOutcome | undefined): boolean {
  return !!outcome && outcome.status !== "failed";
}

export interface ChannelStepPlan {
  intent: WizardChannelIntent;
  connectorId: string;
  listingId: string | null;
  /** True when this attempt must call create_channel_listing. */
  createListing: boolean;
  mediaAssetId: string | null;
  /** True when this attempt must apply the account image after creating/verifying the listing. */
  applyMedia: boolean;
  /**
   * A previous primary-image failure is no longer applicable: the listing
   * exists and the operator intentionally left this account on Product Master
   * inheritance. Emit a resolving outcome instead of replaying a doomed apply.
   */
  resolveMedia: boolean;
}

export interface PlanChannelStepOptions {
  /** Restrict prior outcomes to this product (an attempt is product-scoped). */
  productId?: string | null;
  /**
   * True only when the image library is known-good. A failed / incomplete
   * gallery read is NOT evidence that the override was removed, so the stale
   * media failure must stay visible instead of being silently resolved.
   */
  resolveSupersededMediaFailure?: boolean;
}

/**
 * Decide what each selected account still needs.
 *
 * - A listing that already succeeded is never created twice.
 * - A listing that failed is attempted again.
 * - Media is only applied when the listing exists and the chosen asset is live
 *   in the gallery (the caller supplies the apply plan, which enforces that).
 * - A media step that already succeeded is never repeated for the same listing.
 * - A media step that failed, and is no longer requested because the operator
 *   removed the override, is resolved rather than retried forever.
 */
export function planChannelSteps(
  selected: WizardChannelIntent[],
  prior: ChannelOutcome[],
  mediaByConnector: Map<string, string>,
  opts: PlanChannelStepOptions = {},
): ChannelStepPlan[] {
  const scopedPrior = opts.productId
    ? prior.filter((row) => !row.productId || row.productId === opts.productId)
    : prior;
  return selected.map((intent) => {
    const listing = findChannelOutcome(scopedPrior, intent.connectorId, "listing", opts.productId);
    const media = findChannelOutcome(scopedPrior, intent.connectorId, "primary_media", opts.productId);

    const listingResolved = !!listing && listing.status !== "failed" && !!listing.listingId;
    const listingId = listingResolved ? listing.listingId : null;
    const mediaAssetId = mediaByConnector.get(intent.connectorId) ?? null;
    const supersededMediaFailure = !!listingResolved
      && media?.status === "failed"
      && !mediaAssetId
      && opts.resolveSupersededMediaFailure === true;

    return {
      intent,
      connectorId: intent.connectorId,
      listingId,
      createListing: !listingResolved,
      mediaAssetId,
      // The executor creates/verifies the listing before applying this image.
      // A new listing must not lose its selected image merely because its id
      // was unavailable when this plan was built. Old media success belongs
      // to the resolved listing, so it cannot settle a new listing's work.
      applyMedia: !!mediaAssetId && (!listingResolved || media?.status !== "created"),
      resolveMedia: supersededMediaFailure,
    };
  });
}

/**
 * Fold a retry result over the previous outcomes.
 * A newer outcome replaces the same product+connector+stage. Prior outcomes
 * that were not retried are kept, including genuine failures, so recovery
 * information is never silently dropped. Unrelated failures are untouched.
 */
export function mergeChannelOutcomes(
  prior: ChannelOutcome[],
  next: ChannelOutcome[],
): ChannelOutcome[] {
  const merged = new Map<string, ChannelOutcome>();
  for (const outcome of prior) merged.set(channelOutcomeIdentity(outcome), outcome);
  for (const outcome of next) merged.set(channelOutcomeIdentity(outcome), outcome);
  return [...merged.values()];
}

export function unresolvedChannelOutcomes(outcomes: ChannelOutcome[]): ChannelOutcome[] {
  return outcomes.filter((row) => row.status === "failed");
}

export interface ChannelFailureLine {
  key: string;
  connectorId: string;
  displayName: string;
  stage: ChannelStage;
  /** Stage-accurate heading. A media failure never claims the listing was not saved. */
  heading: string;
  message: string;
}

export function channelFailureLines(outcomes: ChannelOutcome[]): ChannelFailureLine[] {
  return unresolvedChannelOutcomes(outcomes).map((row) => ({
    key: channelOutcomeKey(row.connectorId, row.stage),
    connectorId: row.connectorId,
    displayName: row.displayName,
    stage: row.stage,
    heading: row.stage === "primary_media"
      ? "listing created, primary image not applied"
      : "listing not created",
    message: row.message ?? "unknown error",
  }));
}

/**
 * An `already_exists` response may only be adopted when the returned listing is
 * proven to belong to the expected organisation, product and connector.
 * Anything else is a failure — never a silent success.
 */
export function classifyExistingListing(params: {
  orgId: string;
  productId: string;
  connectorId: string;
  listing: { id?: string | null; org_id?: string | null; product_id?: string | null; connector_id?: string | null } | null;
}): { adopt: boolean; reason: string } {
  if (!params.listing?.id) {
    return { adopt: false, reason: "An existing listing was reported but could not be verified." };
  }
  if (params.listing.org_id && params.listing.org_id !== params.orgId) {
    return { adopt: false, reason: "An existing listing belongs to a different organisation." };
  }
  if (params.listing.product_id && params.listing.product_id !== params.productId) {
    return { adopt: false, reason: "An existing listing belongs to a different product." };
  }
  if (params.listing.connector_id && params.listing.connector_id !== params.connectorId) {
    return { adopt: false, reason: "An existing listing belongs to a different connector account." };
  }
  return { adopt: true, reason: "An existing matching listing was adopted." };
}

/** One line per failed account+stage for the success screen. */
export function channelFailureSummary(outcomes: ChannelOutcome[]): string[] {
  return channelFailureLines(outcomes).map(
    (row) => `${row.displayName}: ${row.heading} — ${row.message}`,
  );
}

/**
 * Verdict for the draft-media transfer (PHASE1-CREATED-PRODUCT-READINESS-001).
 *
 * The transfer RPC returns `{ ok, transferred }`. The wizard previously awaited
 * it and treated ANY resolved promise as success, so a business-level
 * `ok: false` reached the same "everything settled" path as a real transfer and
 * the draft — the only remaining copy of the images — was then discarded.
 *
 * States:
 *   - `transferred`      — rows were copied onto the product this time.
 *   - `already_attached` — `ok` but nothing moved. Legitimate on a repeated
 *                          transfer; it is NOT evidence that images are missing.
 *   - `failed`           — no result, or a result that does not say ok.
 *
 * Only `failed` may be reported as a failure, and only `transferred` /
 * `already_attached` may let the create settle as complete.
 */
export type DraftMediaTransferState = "transferred" | "already_attached" | "failed";

export interface DraftMediaTransferVerdict {
  state: DraftMediaTransferState;
  ok: boolean;
  transferred: number;
  message: string | null;
}

export function classifyDraftMediaTransfer(
  result: { ok?: boolean | null; transferred?: number | null } | null | undefined,
  expectedAssets: number,
): DraftMediaTransferVerdict {
  const expected = Number.isFinite(expectedAssets) ? Math.max(0, Math.trunc(expectedAssets)) : 0;
  if (!result || typeof result !== "object") {
    return {
      state: "failed",
      ok: false,
      transferred: 0,
      message: "The image transfer did not report a result, so the images cannot be confirmed on the product.",
    };
  }
  const transferred = typeof result.transferred === "number" && Number.isFinite(result.transferred)
    ? Math.max(0, Math.trunc(result.transferred))
    : 0;
  if (result.ok !== true) {
    return {
      state: "failed",
      ok: false,
      transferred,
      message: "The images were not transferred to the product.",
    };
  }
  if (transferred === 0 && expected > 0) {
    // A repeated transfer moves nothing because the rows already exist. That is
    // a success, but it is never proof that a first transfer happened.
    return { state: "already_attached", ok: true, transferred, message: null };
  }
  return { state: "transferred", ok: true, transferred, message: null };
}

/**
 * The recovery stage a settled-but-incomplete product needs. A draft kept
 * because its images never moved must resume the `media` path, not a generic
 * post-create one.
 */
export function incompletePostCreateStage(obligations: PostCreateObligations): "media" | "post_create" {
  return obligations.mediaFailures > 0 ? "media" : "post_create";
}

/**
 * What is still outstanding for a created product.
 *
 * `mediaFailures` is the REAL unresolved draft-media transfer count. It used to
 * be passed as a literal 0 by the wizard, which let a product settle — and its
 * draft be discarded — while its images had never reached it. Draft-media
 * transfer failures and channel primary-image failures are counted separately:
 * the latter lives in the connector+stage outcomes above.
 */
export function derivePostCreateObligations(params: {
  outcomes: ChannelOutcome[];
  supplierLinked: boolean;
  mediaFailures: number;
}): PostCreateObligations {
  const media = Number.isFinite(params.mediaFailures) ? Math.trunc(params.mediaFailures) : 0;
  return {
    unresolvedChannels: unresolvedChannelOutcomes(params.outcomes).length,
    mediaFailures: Math.max(0, media),
    supplierPending: !params.supplierLinked,
  };
}